import commonServices from 'ApiService/Common/commonServices';
import { AuthContext } from 'contexts/AuthContext';
import Nodatafound from 'pages/Common/Nodatafound';
import UpdateDataModal from 'pages/Model/UpdateModal';
import { useCallback, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FaRegEdit, FaEye } from 'react-icons/fa';
import { ThreeDots } from 'react-loader-spinner';

const GenericUserList = ({
  fetchUsers,
  updateUser,
  userType,
  columns,
  blockedList,
  onView,
}) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filter, setFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updatedUser, setUpdatedUser] = useState(null);
  const [showFirstConfirmation, setShowFirstConfirmation] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState('');

  const { state } = useContext(AuthContext);

  function reset() {
    setFilteredData(data);
    setSearchTerm('');
    setFilter('all');
    setDeptFilter('all');
    setSortOrder('');
  }
  const selectedBranch = state?.selectedBranch;

  const fetchUserData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchUsers(state?.user?.id, selectedBranch);
      console.log(response, 'smdsmb');
      if (response.data.success) {
        const processedData = response.data.data.map((user) => ({
          ...user,
          branchName:
            user?.branches?.length > 0
              ? user.branches.map((b) => b.name).join(', ')
              : user?.branch?.name || '-',
          branchLocation:
            user?.branches?.length > 0
              ? user.branches.map((b) => b.location).join(', ')
              : user?.branch?.location || '-',
          hrRoleDisplay:
            user?.hrRole === 'headhr'
              ? 'Head HR'
              : user?.hrRole === 'jrhr'
              ? 'JR HR'
              : 'HR',
          departmentName: user?.department?.name || '-',
        }));
        setData(processedData);
        setFilteredData(processedData);
        setLoading(false);
        if (processedData.length === 0) {
          toast.error(`No data found for the ${userType}s`);
        }
      }
    } catch (error) {
      setLoading(false);
      if (error?.response?.data?.message === 'Teachers data not found!') {
        setData([]);
        setFilteredData([]);
      }
      console.error(error?.response?.data?.message, 'error in fetchUserData');
      toast.error(error.response.data.message);
    }
  }, [fetchUsers, state?.user?.id, selectedBranch, userType]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    setFilteredData((prevFilteredData) => {
      const sortedData = [...prevFilteredData].sort((a, b) => {
        const compareValue = a.name.localeCompare(b.name);
        return sortOrder === 'asc' ? compareValue : -compareValue;
      });
      return sortedData;
    });
  }, [sortOrder]);

  useEffect(() => {
    let result = data;
    if (searchTerm) {
      result = result.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.contact &&
            user.contact.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (filter !== 'all') {
      result = result.filter((user) => user.branchName === filter);
    }

    if (deptFilter !== 'all') {
      result = result.filter((user) => user.departmentName === deptFilter);
    }

    if (sortOrder) {
      result.sort((a, b) => {
        const compareValue = a.name.localeCompare(b.name);
        return sortOrder === 'asc' ? compareValue : -compareValue;
      });
    }

    setFilteredData(result);
  }, [data, filter, sortOrder, searchTerm, deptFilter]);

  const handleUpdateUser = async (event) => {
    event.preventDefault();
    try {
      const response = await updateUser(updatedUser);
      if (response.data.success) {
        toast.success(response.data.message);
        setShowUpdateModal(false);
        fetchUserData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.log(error, 'error');
      toast.error(
        error.response?.data?.message || `Failed to update ${userType}`
      );
    }
  };

  const handleActivateStudent = async () => {
    if (!selectedUserId) {
      toast.error('User ID is required');
      return;
    }

    try {
      const response = await commonServices.unblockUser({
        userId: selectedUserId,
        role: userType,
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setShowFirstConfirmation(false);
        fetchUserData();
      } else {
        toast.error(response.data.message);
      }
      setShowFirstConfirmation(false);
      setSelectedUserId(null);
      setSelectedUserName('');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to deactivate user');
      setShowFirstConfirmation(false);
    }
  };

  const handleDeactivateStudent = async () => {
    if (!selectedUserId) {
      toast.error('User ID is required');
      return;
    }

    try {
      const response = await commonServices.blockUser({
        userId: selectedUserId,
        role: userType,
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setShowFirstConfirmation(false);
        fetchUserData();
      } else {
        toast.error(response.data.message);
      }
      setShowFirstConfirmation(false);
      setSelectedUserId(null);
      setSelectedUserName('');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to deactivate user');
      setShowFirstConfirmation(false);
    }
  };
  return (
    <div className="mt-4 h-full w-full md:mt-4">
      {loading ? (
        <div className="flex h-40 items-center justify-center ">
          <ThreeDots color="#5431ed" className="mt-100" />
        </div>
      ) : (
        <>
          <div className="flex flex-col items-center gap-4 sm:flex-row">
            <div className="w-full rounded-xl border border-gray-200 bg-lightPrimary py-2.5 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
              <input
                type="text"
                placeholder="Search by Name, Email, or Contact"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-800 dark:text-white"
              />
            </div>

            <div className="w-full sm:w-[300px]"></div>
            <div className="w-full sm:w-[300px]">
              <div className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
                <select
                  className="text-lightFourth w-full rounded-xl pr-4 text-sm outline-none dark:!bg-navy-800 dark:text-white"
                  name=""
                  id="color"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <option value="">Select Order</option>
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
            </div>

            {state.user.role === 'superadmin' && (
              <div className="w-full sm:w-[300px]">
                <div className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
                  <select
                    className="text-lightFourth w-full rounded-xl pr-4 text-sm outline-none dark:!bg-navy-800 dark:text-white"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  >
                    <option value="all">All Branches</option>
                    {[...new Set(data.map((user) => user.branchName))]
                      .sort((a, b) => a.localeCompare(b))
                      .map((branch) => (
                        <option key={branch} value={branch}>
                          {branch}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            )}

            {userType === 'Teacher' && (
              <div className="w-full sm:w-[300px]">
                <div className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
                  <select
                    className="text-lightFourth w-full rounded-xl pr-4 text-sm outline-none dark:!bg-navy-800 dark:text-white"
                    value={deptFilter}
                    onChange={(e) => setDeptFilter(e.target.value)}
                  >
                    <option value="all">All Departments</option>
                    {[...new Set(data.map((user) => user.departmentName))]
                      .filter((dept) => dept !== '-')
                      .sort((a, b) => a.localeCompare(b))
                      .map((dept) => (
                        <option key={dept} value={dept}>
                          {dept}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            )}

            <div className="w-full sm:w-[200px]">
              <button
                onClick={reset}
                className=" w-40 rounded-xl bg-brand-500 py-3 pl-2.5 pr-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
              >
                Reset
              </button>
            </div>
          </div>
          <div className="mt-[14px] h-full w-full overflow-x-scroll 2xl:overflow-x-hidden">
            <table className="w-full font-dm">
              <thead className="w-full">
                <tr className="items-center border-b border-gray-200 dark:border-white/10">
                  <th className="px-[27px] pb-[12px] pt-[32px] text-start text-xs tracking-wide text-gray-600">
                    <div className="text-xs font-bold">SR. NO.</div>
                  </th>
                  {columns.map((column, index) => (
                    <th
                      key={index}
                      className="px-[27px] pb-[12px] pt-[32px] text-start text-xs tracking-wide text-gray-600"
                    >
                      <div className="text-xs font-bold">
                        {column.header.toUpperCase()}
                      </div>
                    </th>
                  ))}
                  <th className="px-[27px] pb-[12px] pt-[32px] text-start text-xs tracking-wide text-gray-600">
                    <div className="text-xs font-bold">ACTION</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, index) => (
                    <tr
                      className="items-center border-b border-gray-200 dark:border-white/10"
                      key={index}
                    >
                      <td className="mt-[20px] px-[27px] py-[16px]">
                        <div className="w-full text-start font-medium text-navy-700 dark:text-white">
                          {index + 1}
                        </div>
                      </td>
                      {columns.map((column, colIndex) => (
                        <td
                          key={colIndex}
                          className="mt-[20px] px-[27px] py-[16px]"
                        >
                          <div className="w-full text-start font-medium text-navy-700 dark:text-white">
                            {row[column.key] || '-'}
                          </div>
                        </td>
                      ))}
                      <td className="mt-[20px] px-4 py-[16px]">
                        <div className="flex w-full justify-start space-x-4">
                          {onView && (
                            <button
                              onClick={() => onView(row)}
                              className="flex items-center justify-center rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                              title="View Details"
                            >
                              <FaEye />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setUpdatedUser({ ...row });
                              setShowUpdateModal(true);
                              setModalTitle(`Update ${row.name} Details`);
                            }}
                            className="flex items-center justify-center rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                          >
                            <FaRegEdit />
                          </button>
                          {/* {state?.user?.role !== 'superadmin' && ( */}
                          <button
                            onClick={() => {
                              setSelectedUserId(row._id);
                              setShowFirstConfirmation(true);
                              setSelectedUserName(row.name);
                            }}
                            className="flex items-center justify-center rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                          >
                            -
                          </button>
                          {/* )} */}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                      <td
                        colSpan={columns.length + 2}
                        className="py-4 text-center text-gray-500"
                      >
                        <div className="text-lg font-bold text-navy-700 dark:text-white">
                          <p className="mt-0 text-center">
                            <Nodatafound title={`No ${userType} data found`} />
                          </p>
                        </div>
                      </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {showUpdateModal && (
            <UpdateDataModal
              updatedData={updatedUser}
              setUpdatedData={setUpdatedUser}
              setShowUpdateModal={setShowUpdateModal}
              handleUpdateData={handleUpdateUser}
              title={modalTitle}
              userType={userType}
            />
          )}
          {showFirstConfirmation && (
            <div className="bg-black fixed inset-0 flex items-center justify-center bg-opacity-50">
              <div className="mx-4 w-full max-w-md rounded-lg bg-white p-4 shadow-lg dark:bg-navy-800 md:mx-0 md:p-8">
                <div className="flex items-start">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 rounded-full border border-blue-100 bg-blue-50 p-2 text-blue-400 md:h-16 md:w-16 md:p-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="ml-3 flex-1 md:ml-4">
                    {blockedList ? (
                      <>
                        <h2 className="text-base font-medium dark:text-white md:text-lg">
                          Activate {selectedUserName}'s Account?
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                          Are you sure you want to activate {selectedUserName}'s
                          account?
                        </p>
                      </>
                    ) : (
                      <>
                        <h2 className="text-base font-medium dark:text-white md:text-lg">
                          Deactivate {selectedUserName}'s Account?
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                          Are you sure you want to deactivate {selectedUserName}
                          's account?
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <div className="mt-6 flex justify-end">
                  {blockedList ? (
                    <button
                      onClick={handleActivateStudent}
                      className="ml-3 rounded-full border-2 border-blue-500 bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition duration-200 hover:bg-blue-600 hover:shadow-lg"
                    >
                      Activate
                    </button>
                  ) : (
                    <button
                      onClick={handleDeactivateStudent}
                      className="ml-3 rounded-full border-2 border-red-500 bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition duration-200 hover:bg-red-600 hover:shadow-lg"
                    >
                      Deactivate
                    </button>
                  )}
                  <button
                    onClick={() => setShowFirstConfirmation(false)}
                    className="ml-3 rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition duration-200 hover:bg-gray-100 hover:shadow-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GenericUserList;
